import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { Warranty } from '@econtractor/types';
import * as api from 'api/apiWarranties';
import { useGlobal } from 'context/GlobalContext';
import { useToast } from 'context/ToastContext';
import { useModal } from 'context/ModalContext';
import { getButtonsNoYesDanger } from 'context/helpers/helperModalContext';
import { useListOnPage } from 'utils/helpers/list/useListOnPage';
import { useWarrantyRegistry } from '../context/WarrantyProvider';

const useWarranties = () => {
    const state = useWarrantyRegistry();
    const { g } = useGlobal();
    const { pathname } = useLocation();
    const { showToast } = useToast();
    const { t } = useTranslation(['Warranties', 'common']);
    const { showDlg } = useModal();
    const onPage = useListOnPage();
    const page = g.paginator?.[pathname]?.currentPage || 1;
    const search = g.search?.[pathname] || '';
    const [rows, setRows] = useState<Warranty[] | null>(null);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const request = useRef(0);
    const { query, revision } = state;
    const setPaginator = g.setPaginator;
    const feedback = useRef({ showToast, t });
    feedback.current = { showToast, t };
    const queryKey = JSON.stringify(query);
    const previousQuery = useRef(queryKey + search);
    useEffect(() => {
        if (previousQuery.current !== queryKey + search) {
            previousQuery.current = queryKey + search;
            if (page !== 1) {
                setPaginator((prev: Record<string, unknown>) => ({
                    ...prev,
                    [pathname]: { ...((prev[pathname] as object) || {}), currentPage: 1 },
                }));
                return;
            }
        }
        let cancelled = false;
        const sequence = ++request.current;
        setLoading(true);
        void api
            .list({ ...query, page, onPage, search })
            .then((result) => {
                if (cancelled || sequence !== request.current) return;
                setError('');
                setRows(result.warranties);
                setPaginator((prev: Record<string, unknown>) => ({
                    ...prev,
                    [pathname]: result.paginator,
                }));
            })
            .catch((error) => {
                if (!cancelled && sequence === request.current) {
                    const message = feedback.current.t(`errors.${api.errorCode(error)}`);
                    setError(message);
                    feedback.current.showToast(message, 'danger');
                }
            })
            .finally(() => {
                if (!cancelled && sequence === request.current) setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [query, queryKey, page, onPage, search, revision, pathname, setPaginator]);
    const archiveWarranty = async (id: number, archived: boolean) => {
        setLoading(true);
        try {
            await api.archive(id, archived);
        } catch (error) {
            feedback.current.showToast(
                feedback.current.t(`errors.${api.errorCode(error)}`),
                'danger',
            );
        } finally {
            state.refresh();
            setLoading(false);
        }
    };
    const deleteWarranty = (id: number) => {
        const buttons = getButtonsNoYesDanger(t);
        buttons[1].handleClick = () => {
            showDlg(false);
            setLoading(true);
            void api
                .remove(id)
                .then(() => {
                    state.refresh();
                })
                .catch((error) => {
                    feedback.current.showToast(
                        feedback.current.t(`errors.${api.errorCode(error)}`),
                        'danger',
                    );
                    state.refresh();
                })
                .finally(() => setLoading(false));
        };
        showDlg({ type: 'confirm', text: t('confirmDelete'), buttons });
    };
    return { ...state, rows, loading, error, deleteWarranty, archiveWarranty };
};

export default useWarranties;
